import { useMemo, useState } from "react";
import { useDebounce } from "@reactuses/core";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { useBlockWindowQuery } from "../../../components/table/useBlockWindowQuery";
import { useAppSelector } from "../../../redux/hooks";
import { useGetV4ProductReportByGrouping } from "../api/product/v4ProductReportByGrouping";
import { css } from "../../../../styled-system/css";
import { useServerTable, type AppRow, type AppColumns } from "../../../components/table/tableFeatures";
import {
  type ManagedTableState,
  type ManagedTableStateSetters,
  usePersistedTableState,
} from "../../../components/table/usePersistedTableState";
import { Flex, HStack, VStack, Wrap } from "../../../../styled-system/jsx";
import { RT8ColumnSelector } from "../../../components/core/blocks/MetricSelector";
import { SearchInput } from "../../../components/fields/base/TextField";
import { RT8Pagination } from "../../../components/table/Pagination";
import { Table } from "../../../components/table/Table";
import { idsToVisibilityState, resetTable, selectionStateToSelectedIds } from "../../../components/table/utils";
import { RT8FilterWidget } from "../../merchandising/components/tables/FilterWidget";
import { DataViewPicker } from "../../dataViews/components/DataViewPicker";
import { Button } from "../../../components/core/basic/Button";
import { MdRotateLeft } from "react-icons/md";
import { useCategoriesQuery } from "../../../hooks/useCategories";
import { useGetV4ProductReportOptions } from "../api/product/v4ProductReportOptions";
import { INVENTORY_STATE_OPTIONS, SALE_STATE_OPTIONS } from "../../../components/custom/merchandise/constants";
import { DownloadV4Report } from "../DownloadReport";
import { useV4ProductReportExport } from "../api/product/v4ProductReportExport";
import { ENROLL_OPTIONS } from "../../advertising/filters/productLifecycleFilters";
import { toTitleCase } from "../../../utils/formatText";
import { BulkActionButton } from "../../../components/table/BulkActionButton";
import BulkTagsForm from "../../../components/core/blocks/BulkTagsForm";
import { useBulkUpdateAllProductAdStatuses } from "../../advertising/api/useBulkUpdateAllProductAdStatuses";
import { useBulkUpdateProductProperties } from "../../advertising/api/useBulkUpdateProductProperties";
import { TableErrorWrapper } from "../../../components/table/TableErrorWrapper";
import { useSimpleCan } from "../../../config/casl/casl";
import { Select } from "../../../components/fields/base/Select";
import { type ProductReportGrouping } from "../../../zodios/common/productReportGroupingSchema";
import { getRowIdForGrouping, PRODUCT_REPORT_GROUPING_OPTIONS } from "./productReportGroupings";
import { type ProductReportRow, useProductReportColumns } from "./useProductReportColumns";
import { useModuleEnabled } from "../../../redux/selectors/modules";
import { useTagFilterOptions } from "../../tags/api/useTagFilterOptions";
import { EntityTagsFormModal } from "../../tags/components/EntityTagsFormModal";
import NiceModal from "@ebay/nice-modal-react";
import { ExpandedContent } from "../../../components/custom/merchandise/ExpandedContent";
import { type ProductGroupedQueryV4Row } from "../api/product/v4ProductReportByGrouping";

const EXPANDER_DEFAULT_COLUMNS = [
  "product_title",
  "price",
  "units",
  "sales",
  "total_acos",
  "page_views",
  "ad_sales",
  "ad_spend",
  "acos",
  "conversion_rate",
  "total_conv_rate",
  "orders",
  "ctr",
  "clicks",
  "impressions",
];

const GroupedRowExpander = ({
  row,
  grouping,
}: {
  row: AppRow<ProductGroupedQueryV4Row>;
  grouping: ProductReportGrouping;
}) => {
  // Build filter based on grouping type
  const filterField = grouping === "parent_asin_defaulted" ? "parent_asin_defaulted" : "asin";
  const filterValue = grouping === "parent_asin_defaulted" ? row.original.parent_asin_defaulted : row.original.asin;

  const columns = useProductReportColumns(undefined, "product_id");

  const defaultHiddenCols = idsToVisibilityState(
    EXPANDER_DEFAULT_COLUMNS,
    columns.filter((column) => Boolean(column.id)).map((column) => column.id as string),
  );

  const { setters, state, initialState } = usePersistedTableState({
    persistKey: `productReportExpander`,
    initialState: {
      sorting: [{ id: "total_sales", desc: true }],
      pagination: { pageIndex: 0, pageSize: 50 },
      columnVisibility: defaultHiddenCols,
      columnPinning: {
        start: ["product_title"],
        end: [],
      },
    },
  });

  const parentFilter = filterValue ? [{ id: filterField, value: { op: "=", value: filterValue } }] : [];

  return (
    <ProductReportTable
      state={state}
      initialState={initialState}
      setters={setters}
      grouping="product_id"
      extraFilters={parentFilter}
      columns={columns}
    />
  );
};

const ProductReportTable = ({
  columns,
  state,
  initialState,
  setters,
  grouping,
  onGroupingChange,
  extraFilters = [],
}: {
  columns: AppColumns<ProductReportRow>;
  state: ManagedTableState;
  initialState: ManagedTableState;
  setters: ManagedTableStateSetters;
  grouping: ProductReportGrouping;
  onGroupingChange?: (grouping: string) => void;
  extraFilters?: { id: string; value: unknown }[];
}) => {
  const { t } = useTranslation(["perf_reports"]);
  const isGroupedView = grouping !== "product_id";
  const [filledTags, setFilledTags] = useState<string[]>([]);
  const tagsEnhancedEnabled = useModuleEnabled("TAGS_ENHANCED");

  const user = useAppSelector((state) => state.user);
  const marketPlace = useAppSelector((state) => state.marketPlace);
  const channel = useAppSelector((state) => state.channel.currentChannel);
  const userCanUpdateProducts = useSimpleCan("update", "Product");
  const userCanExportProducts = useSimpleCan("export", "Product");

  const debounceSearch = useDebounce(state.globalFilter, 500);
  const allFilters = [...extraFilters, ...state.columnFilters];

  const byGroupingOptionsGetter = useGetV4ProductReportByGrouping(grouping);
  const productReportQuery = useBlockWindowQuery(byGroupingOptionsGetter, {
    pagination: state.pagination,
    sorting: state.sorting,
    filters: allFilters,
    search: debounceSearch,
  });

  const categoriesQuery = useCategoriesQuery();
  const exportMutation = useV4ProductReportExport(state.columnFilters);
  const productReportOptionsQuery = useQuery(useGetV4ProductReportOptions());
  const tagEnhancedOptions = useTagFilterOptions("PRODUCT", { enabled: tagsEnhancedEnabled });

  const tagOptions = useMemo(
    () => [
      { value: "all", label: t(($) => $.perf_reports["All Tags"]) },
      ...(productReportOptionsQuery.data?.data.tags.map((tag) => {
        return {
          value: tag,
          label: tag,
        };
      }) ?? []),
    ],
    [productReportOptionsQuery.data?.data.tags, t],
  );

  const variantOptions = useMemo(() => {
    const { variations } = productReportOptionsQuery.data?.data ?? {};

    if (!variations) {
      return [];
    }

    return [
      { value: "all", label: t(($) => $.perf_reports["All Variants"]) },
      ...Object.keys(variations).flatMap((variationCategory) => {
        return (
          variations[variationCategory]?.map((variation) => ({
            label: `${toTitleCase(variationCategory)}: ${variation}`,
            value: { [variationCategory]: variation },
          })) ?? []
        );
      }),
    ];
  }, [productReportOptionsQuery.data?.data, t]);

  const categoryOptions = useMemo(
    () => [
      { value: "all", label: t(($) => $.perf_reports["All Categories"]) },
      ...(categoriesQuery.data?.map((category) => {
        return {
          value: category.id.toString(),
          label: category.leaf_category_name,
        };
      }) ?? []),
    ],
    [categoriesQuery.data, t],
  );
  const table = useServerTable({
    data: productReportQuery.data?.data,
    columns,
    alternateSortingEnabled: true,
    getRowCanExpand: () => true,
    getRowId: (row) => getRowIdForGrouping(row, grouping),
    enableRowSelection: !isGroupedView,
    rowCount: productReportQuery.data?.count || 0,
    initialState: initialState,
    state: state,
    ...setters,
    meta: {
      showSkeleton: productReportQuery.isFetching && (!productReportQuery.data || productReportQuery.isPlaceholderData),
      columnFilterSelectOptions: {
        category_id: categoryOptions,
        tags: tagOptions,
        "tags_enhanced.id": tagEnhancedOptions,
        variations: variantOptions.filter(Boolean),
        has_ads: [
          {
            value: true,
            label: t(($) => $.perf_reports["Active Ads"]),
          },
          {
            value: false,
            label: t(($) => $.perf_reports["No Active Ads"]),
          },
        ],
        inventory_status: [
          {
            value: "all",
            label: t(($) => $.perf_reports["All Inventory States"]),
            description: t(($) => $.perf_reports["Show products with any inventory state"]),
          },
          ...INVENTORY_STATE_OPTIONS.filter(Boolean),
        ],
        pricing_plan_status: ENROLL_OPTIONS,
        sales_state: [
          {
            value: "all",
            label: t(($) => $.perf_reports["All Sales States"]),
          },
          ...SALE_STATE_OPTIONS.filter((option) => option !== undefined),
        ],
      },
    },
  });

  const bulkUpdateProductAdStatusMutation = useBulkUpdateAllProductAdStatuses();
  const bulkUpdateProductPropertiesMutation = useBulkUpdateProductProperties();

  const updateProductAdStatus = (newStatus: string) => {
    const productData = {
      product_ids: selected.map((id) => parseInt(id)),
      status: newStatus,
    };

    bulkUpdateProductAdStatusMutation.mutate(productData);
  };

  const selected = selectionStateToSelectedIds(table.state.rowSelection);

  const groupingItems = useMemo(
    () => PRODUCT_REPORT_GROUPING_OPTIONS.map((opt) => ({ value: opt.value, label: t(opt.label) })),
    [t],
  );

  return (
    <div>
      <Wrap justifyContent={"end"} width={"100%"} padding={"small"} fontSize={"large"}>
        <BulkActionButton
          isDisabled={!userCanUpdateProducts || isGroupedView}
          selected={selected}
          options={[
            ...(tagsEnhancedEnabled ?
              [
                {
                  label: t(($) => $.perf_reports["Add Product Tags"]),
                  action: () => {
                    void NiceModal.show(EntityTagsFormModal, {
                      entityType: "PRODUCT" as const,
                      title: t(($) => $.perf_reports["Add Product Tags"]),
                      onSubmit: async (tagIds: number[]) => {
                        await bulkUpdateProductPropertiesMutation.mutateAsync({
                          product_ids: selected.map((id) => parseInt(id)),
                          updates: [{ field: "tags", action: "add", value: tagIds }],
                        });
                      },
                    });
                  },
                },
                {
                  label: t(($) => $.perf_reports["Remove Product Tags"]),
                  action: () => {
                    void NiceModal.show(EntityTagsFormModal, {
                      entityType: "PRODUCT" as const,
                      title: t(($) => $.perf_reports["Remove Product Tags"]),
                      onSubmit: async (tagIds: number[]) => {
                        await bulkUpdateProductPropertiesMutation.mutateAsync({
                          product_ids: selected.map((id) => parseInt(id)),
                          updates: [{ field: "tags", action: "remove", value: tagIds }],
                        });
                      },
                    });
                  },
                },
              ]
            : [
                {
                  label: t(($) => $.perf_reports["Add Product Tags"]),
                  action: () => {
                    bulkUpdateProductPropertiesMutation.mutate(
                      {
                        product_ids: selected.map((id) => parseInt(id)),
                        updates: [
                          {
                            field: "tags" as const,
                            action: "add" as const,
                            value: filledTags,
                          },
                        ],
                      },
                      { onSuccess: () => setFilledTags([]) },
                    );
                  },
                  actionFormFields: (
                    <BulkTagsForm
                      filledTags={filledTags}
                      setFilledTags={setFilledTags}
                      selected={selected}
                      suggestedTags={tagOptions.filter((t) => t.value !== "all").map((tag) => tag.value)}
                      taggedItemDescription={"product"}
                      actionType={"add"}
                    />
                  ),
                  confirmationMessage: (
                    <p>
                      {t(
                        ($) =>
                          $.perf_reports[
                            "Are you sure you want to add the selected tag(s) to the selected product(s)?"
                          ],
                        {
                          tagCount: filledTags.length,
                          productCount: selected.length,
                        },
                      )}
                    </p>
                  ),
                },
                {
                  label: t(($) => $.perf_reports["Remove Product Tags"]),
                  action: () => {
                    bulkUpdateProductPropertiesMutation.mutate(
                      {
                        product_ids: selected.map((id) => parseInt(id)),
                        updates: [
                          {
                            field: "tags" as const,
                            action: "remove" as const,
                            value: filledTags,
                          },
                        ],
                      },
                      { onSuccess: () => setFilledTags([]) },
                    );
                  },
                  actionFormFields: (
                    <BulkTagsForm
                      filledTags={filledTags}
                      setFilledTags={setFilledTags}
                      selected={selected}
                      suggestedTags={tagOptions.filter((t) => t.value !== "all").map((tag) => tag.value)}
                      taggedItemDescription={"product"}
                      actionType={"remove"}
                    />
                  ),
                  confirmationMessage: (
                    <p>
                      {t(
                        ($) =>
                          $.perf_reports[
                            "Are you sure you want to remove the selected tag(s) from the selected product(s)?"
                          ],
                        { tagCount: filledTags.length, productCount: selected.length },
                      )}
                    </p>
                  ),
                },
              ]),
            {
              label: t(($) => $.perf_reports["Pause Product Advertisements"]),
              action: () => {
                updateProductAdStatus("paused");
              },
              confirmationMessage: (
                <p>
                  {t(
                    ($) =>
                      $.perf_reports[
                        "Are you sure you want to pause all product advertisements for the selected product(s)?"
                      ],
                    { count: selected.length },
                  )}
                </p>
              ),
            },
            {
              label: t(($) => $.perf_reports["Enable Product Advertisements"]),
              action: () => {
                updateProductAdStatus("enabled");
              },
              confirmationMessage: (
                <p>
                  {t(
                    ($) =>
                      $.perf_reports[
                        "Are you sure you want to enable all product advertisements for the selected product(s)?"
                      ],
                    { count: selected.length },
                  )}
                </p>
              ),
            },
          ]}
        />
      </Wrap>
      <HStack padding={"small"} fontSize={"large"}>
        <VStack flex="1" alignItems={"start"}>
          <HStack width={"100%"}>
            <Button variant={"contentOnly"} onPress={() => resetTable(table, "productReport")} fontSize={"xxlarge"}>
              <MdRotateLeft />
            </Button>
            <RT8ColumnSelector table={table} />
            <DataViewPicker viewKey="productReport" datasetKey="product" table={table} />
            <SearchInput
              aria-label={t(($) => $.perf_reports["Search"])}
              isExpandable
              css={{ flex: "0 1 350px" }}
              value={table.state.globalFilter}
              onChange={table.setGlobalFilter}
            />
            {onGroupingChange && channel === "amazon" && (
              <Flex className={css({ alignSelf: "end", width: "400px", marginBottom: "large", paddingX: "medium" })}>
                <Select
                  label={t(($) => $.perf_reports["Group By"])}
                  value={grouping}
                  onChange={(key) => {
                    if (key !== null) {
                      onGroupingChange(key.toString());
                    }
                  }}
                  items={groupingItems}
                />
              </Flex>
            )}
          </HStack>
          <RT8FilterWidget table={table} />
        </VStack>
        <RT8Pagination className={css({ marginLeft: "auto" })} table={table} />
      </HStack>
      <TableErrorWrapper
        error={productReportQuery.error}
        userMessage={t(($) => $.perf_reports["Error loading product report."])}
      >
        <Table
          table={table}
          className={css({ minWidth: "100%" })}
          renderExpander={(row) =>
            isGroupedView ?
              <GroupedRowExpander row={row} grouping={grouping} />
            : <ExpandedContent
                marketPlace={marketPlace}
                row={{ original: { product_id: row.original.product_id?.toString() } }}
                user={user}
                channel={channel}
              />
          }
        />
      </TableErrorWrapper>
      {userCanExportProducts && (
        <HStack width={"100%"} padding={"small"}>
          <DownloadV4Report downloadMutation={exportMutation} />
        </HStack>
      )}
    </div>
  );
};

export default ProductReportTable;
